import api from "./api";
import {
  Book,
  BookCreate,
  BookPage,
  BookUpdate,
  BookAbandon,
  BookResume,
  SeriesContext,
  SeriesCorrectionUpdate,
  EditionProgress,
  BulkLibraryOperation,
  BulkLibraryResult,
  UndoReceipt,
  UndoResult,
  BookReadingSummary,
  CatalogEditionSearchPage,
  EditionConversionMode,
  EditionSwitchPreview,
  EditionSwitchResult,
} from "../types";
import type { BookListFilters } from "../queries/keys";
import { conditionalHeaders } from "./concurrency";

const BOOKS_ENDPOINT = "/books/";

export function serializeBookListFilters(filters: BookListFilters): URLSearchParams {
  const searchParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
      return;
    }
    searchParams.set(key, String(value));
  });
  return searchParams;
}

function bookEndpoint(id: number): string {
  return `/books/${id}`;
}

export const bookService = {
  // Get all books
  async getAllBooks(
    params: BookListFilters = {},
    signal?: AbortSignal
  ): Promise<BookPage> {
    const response = await api.get<BookPage>(BOOKS_ENDPOINT, {
      params: serializeBookListFilters(params),
      signal,
    });
    return response.data;
  },

  // Get a single book by ID
  async getBookById(id: number, signal?: AbortSignal): Promise<Book> {
    const response = await api.get<Book>(bookEndpoint(id), { signal });
    return response.data;
  },

  async getReadingSummary(
    id: number,
    signal?: AbortSignal
  ): Promise<BookReadingSummary> {
    const response = await api.get<BookReadingSummary>(
      `${bookEndpoint(id)}/reading-summary`,
      { signal }
    );
    return response.data;
  },

  async getBookSeries(id: number, signal?: AbortSignal): Promise<SeriesContext> {
    const response = await api.get<SeriesContext>(`${bookEndpoint(id)}/series`, {
      signal,
    });
    return response.data;
  },

  async getEditionProgress(id: number, signal?: AbortSignal): Promise<EditionProgress> {
    const response = await api.get<EditionProgress>(
      `${bookEndpoint(id)}/edition-progress`,
      { signal }
    );
    return response.data;
  },

  async updateBookSeries(
    id: number,
    update: SeriesCorrectionUpdate
  ): Promise<SeriesContext | null> {
    const response = await api.patch<SeriesContext | null>(
      `${bookEndpoint(id)}/series`,
      update
    );
    return response.data;
  },

  // Create a new book
  async createBook(bookData: BookCreate): Promise<Book> {
    const response = await api.post<Book>(BOOKS_ENDPOINT, bookData);
    return response.data;
  },

  // Update a book
  async updateBook(
    id: number,
    bookData: BookUpdate,
    updatedAt?: string
  ): Promise<Book> {
    const response = await api.put<Book>(bookEndpoint(id), bookData, {
      headers: conditionalHeaders(updatedAt),
    });
    return response.data;
  },

  async searchEditions(query: string): Promise<CatalogEditionSearchPage> {
    const response = await api.get<CatalogEditionSearchPage>("/catalog/editions", {
      params: { q: query, limit: 20 },
    });
    return response.data;
  },

  async previewEditionSwitch(
    id: number,
    targetCatalogEditionId: number
  ): Promise<EditionSwitchPreview> {
    const response = await api.post<EditionSwitchPreview>(
      `${bookEndpoint(id)}/edition-switch/preview`,
      { contract_version: "v1", target_catalog_edition_id: targetCatalogEditionId }
    );
    return response.data;
  },

  async confirmEditionSwitch(
    book: Book,
    preview: EditionSwitchPreview,
    conversionMode: EditionConversionMode
  ): Promise<EditionSwitchResult> {
    const response = await api.post<EditionSwitchResult>(
      `${bookEndpoint(book.id)}/edition-switch/confirm`,
      {
        contract_version: "v1",
        target_catalog_edition_id: preview.target_catalog_edition_id,
        conversion_mode: conversionMode,
        expected_source_catalog_edition_id: preview.source_catalog_edition_id,
        expected_current_page: preview.current_page,
        confirmed: true,
      },
      { headers: conditionalHeaders(book.updated_at) }
    );
    return response.data;
  },

  async abandonBook(id: number, data: BookAbandon, updatedAt?: string): Promise<Book> {
    const response = await api.post<Book>(`${bookEndpoint(id)}/abandon`, data, {
      headers: conditionalHeaders(updatedAt),
    });
    return response.data;
  },

  async resumeBook(id: number, data: BookResume, updatedAt?: string): Promise<Book> {
    const response = await api.post<Book>(`${bookEndpoint(id)}/resume`, data, {
      headers: conditionalHeaders(updatedAt),
    });
    return response.data;
  },

  // Delete a book
  async deleteBook(id: number, updatedAt?: string): Promise<UndoReceipt> {
    const response = await api.delete<UndoReceipt>(bookEndpoint(id), {
      headers: conditionalHeaders(updatedAt),
    });
    return response.data;
  },

  async bulkUpdate(payload: BulkLibraryOperation): Promise<BulkLibraryResult> {
    const response = await api.post<BulkLibraryResult>("/books/bulk", payload);
    return response.data;
  },

  async undo(token: string): Promise<UndoResult> {
    const response = await api.post<UndoResult>(`/books/undo/${token}`);
    return response.data;
  },

  async getUndoHistory(signal?: AbortSignal): Promise<UndoReceipt[]> {
    const response = await api.get<UndoReceipt[]>("/books/undo", { signal });
    return response.data;
  },

  async getArchivedBooks(signal?: AbortSignal): Promise<Book[]> {
    const response = await api.get<Book[]>("/books/archived", { signal });
    return response.data;
  },

  // Mark book for exchange
  async markForExchange(
    id: number,
    data: {
      available_for_exchange?: boolean;
      available_for_sale?: boolean;
      exchange_price?: number;
      sale_price?: number;
    }
  ): Promise<Book> {
    const response = await api.post<Book>(`${bookEndpoint(id)}/exchange`, data);
    return response.data;
  },

  // Search books with filters
};
